import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";
import { useEffect, useState } from "react";
import { getGroupProfiles, addProfileToGroup } from "utils/groupUtils";
import { capitalize } from "@mui/material";
import { UserName } from "components/alove/UserName";
import { TwoSizeChip } from "components/alove/Chips";
import { formatDateTime } from "utils/globalsUtils";
import { Profile } from "models/profile/Profile";
import { Icon, Button, TextField, Box, Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { addAlert, useMaterialUIController } from "context";
import { getNamesAsync } from "utils/enduserUtils";


const MDInput = require("components/MDInput").default;
const MDButton = require("components/MDButton").default;

interface GroupUsersListProps {
  groupId: string;
}

export const GroupUsersList = ({ groupId }: GroupUsersListProps) => {
  const [users, setUsers] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [profileId, setProfileId] = useState("");
  const [addingProfile, setAddingProfile] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [controller, dispatch] = useMaterialUIController();

  const fetchUsers = () => {
    if (groupId) {
      setLoading(true);
      getGroupProfiles(groupId)
        .then((profiles) => {
          getNamesAsync(profiles.map((p) => p.userId))
          setUsers(profiles);
        })
        .catch((error) => {
          console.error("Error fetching group profiles:", error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [groupId]);

  const handleAddProfile = async () => {
    if (!profileId.trim()) {
      setError("Please enter a profile ID");
      return;
    }

    setAddingProfile(true);
    setError("");

    try {
      await addProfileToGroup(groupId, profileId.trim());
      addAlert(dispatch, "Profile added to group successfully");
      setProfileId("");
      fetchUsers(); // Refresh the list
    } catch (error) {
      setError("Failed to add profile to group. Please check the profile ID and try again.");
      console.error("Error adding profile to group:", error);
    } finally {
      setAddingProfile(false);
    }
  };

  const columns: GridColDef[] = [
    {
      headerName: "First Name",
      field: "firstName",
      flex: 1,
      minWidth: 100,
      renderCell: (params) => <UserName userId={params.row.userId} />,
    },
    {
      headerName: "Gender",
      field: "gender",
      valueGetter: (v: string, p: Profile) => capitalize(p.attributesValues?.gender?.[0] || ""),
    },
    {
      headerName: "Type",
      field: "type",
      valueGetter: (v: string, p: Profile) =>
        capitalize(p.attributesValues?.userType?.[0]?.toString() || ""),
    },
    {
      headerName: "Created",
      field: "created",
      flex: 1,
      minWidth: 170,
      align: "center",
      renderCell: (params) => formatDateTime(params.row.created),
    },
    {
      headerName: "Last Suggestion",
      field: "lastSuggestion",
      flex: 1,
      minWidth: 170,
      align: "center",
      renderCell: (params) => formatDateTime(params.row.lastSuggestion),
    },
    {
      headerName: "Last Login",
      field: "lastSuccessfulLogin",
      flex: 1,
      minWidth: 170,
      align: "center",
      renderCell: (params) => formatDateTime(params.row.lastSuccessfulLogin),
    },
    {
      headerName: "Status",
      field: "statusId",
      flex: 1,
      minWidth: 100,
      align: "center",
      renderCell: (params) => {
        const user = Object.assign(new Profile(), params.row);
        return <TwoSizeChip label={user.statusLabel} color={user.statusColor as any} />;
      },
    },
    {
      headerName: "Actions",
      field: "actions",
      flex: 1,
      align: "center",
      renderCell: (params) => (
        <Icon onClick={() => navigate(`/endUsers/${params.id}`)}>edit</Icon>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <MDInput
          label="Profile ID"
          value={profileId}
          onChange={(e) => setProfileId(e.target.value)}
          placeholder="Enter profile ID"
        />
        <MDButton
          variant="contained"
          onClick={handleAddProfile}
          color="secondary"
          disabled={addingProfile || !profileId.trim()}
        >
          {addingProfile ? "Adding..." : "Add Profile to Group"}
        </MDButton>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <DataGridPro
        rows={users}
        columns={columns}
        loading={loading}
        autoHeight
        disableRowSelectionOnClick
        getRowId={(row) => row.id || row.profileId}
      />
    </Box>
  );
}; 